import React from 'react';
import { Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import { ComplianceField } from 'src/Lca/compliance';

type TechnicalSpecification = {
  name: string;
  range: string;
};

const emptyTechnicalSpecification: TechnicalSpecification = {
  name: '',
  range: '',
};

const parseTechnicalSpecifications = (
  rawValue: string
): TechnicalSpecification[] => {
  if (!rawValue) {
    return [emptyTechnicalSpecification];
  }
  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [emptyTechnicalSpecification];
    }
    return parsed.map((entry) => ({
      name: entry?.name || '',
      range: entry?.range || '',
    }));
  } catch {
    return [emptyTechnicalSpecification];
  }
};

const serializeTechnicalSpecifications = (
  technicalSpecifications: TechnicalSpecification[]
) => {
  const hasAnyFilled = technicalSpecifications.some(
    (entry) => entry.name || entry.range
  );

  // Keep empty rows when user adds multiple items, so Add next specification works.
  if (!hasAnyFilled && technicalSpecifications.length === 1) {
    return '';
  }
  return JSON.stringify(technicalSpecifications);
};

export default function TechnicalSpecifications({
  setValues,
  values,
}: ProductInformationProps) {
  const { t } = useTranslation('lca', {
    keyPrefix: 'technicalSpecifications',
  });
  const technicalSpecifications = parseTechnicalSpecifications(
    values.technical_specifications || ''
  );

  const setTechnicalSpecifications = (nextItems: TechnicalSpecification[]) => {
    setValues({
      ...values,
      technical_specifications: serializeTechnicalSpecifications(nextItems),
    });
  };

  const updateSpecification = (
    index: number,
    key: keyof TechnicalSpecification,
    value: string
  ) => {
    const nextItems = technicalSpecifications.map((entry, i) =>
      i === index ? { ...entry, [key]: value } : entry
    );
    setTechnicalSpecifications(nextItems);
  };

  const addSpecification = () =>
    setTechnicalSpecifications([
      ...technicalSpecifications,
      emptyTechnicalSpecification,
    ]);

  const removeSpecification = (index: number) => {
    const nextItems = technicalSpecifications.filter((_, i) => i !== index);
    setTechnicalSpecifications(
      nextItems.length ? nextItems : [emptyTechnicalSpecification]
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {technicalSpecifications.map((entry, index) => (
        <Box
          key={index}
          sx={{
            border: '1px solid #ececec',
            borderRadius: '12px',
            p: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <ComplianceField
            excelRow={37}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('name')}
              value={entry.name}
              onChange={(e) =>
                updateSpecification(index, 'name', e.target.value)
              }
              placeholder={t('namePlaceholder')}
            />
          </ComplianceField>
          <ComplianceField
            excelRow={38}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('valueRange')}
              value={entry.range}
              onChange={(e) =>
                updateSpecification(index, 'range', e.target.value)
              }
              placeholder={t('valueRangePlaceholder')}
            />
          </ComplianceField>
          <Box>
            <Button
              variant="secondary"
              size="small"
              onClick={() => removeSpecification(index)}
              disabled={technicalSpecifications.length === 1}
            >
              {t('remove')}
            </Button>
          </Box>
        </Box>
      ))}

      <Box>
        <Button variant="secondary" size="small" onClick={addSpecification}>
          {t('add')}
        </Button>
      </Box>
    </Box>
  );
}
